/**
 * COD form THEME model + template registries (feature 0026 — the Form designer's "Templates").
 *
 * The visual look of the storefront COD order form is captured as a flat bag of design tokens
 * ({@link CodFormTheme}) — colours, button and corner radii. A "template" is a named preset that is
 * MERGED into the current theme, along the two axes Releasit exposes:
 *  - a SHAPE ({@link SHAPE_TEMPLATES}) patches only the corner radii (non-destructive to colours);
 *  - a THEME ({@link THEME_TEMPLATES}) patches the full colour palette but keeps the seller's radii.
 * The form's STRUCTURE (which fields/blocks) lives elsewhere ({@link ./cod-config}); a template never
 * touches it. At render time {@link resolveThemeVars} turns the tokens into `--cf-*` CSS custom
 * properties — one variable-driven stylesheet — the same resolver pattern as the storefront theme
 * settings (feature 0019). Adding a new template is one entry here, zero component code.
 *
 * Pure + framework-free, like {@link ./cod-config}: {@link parseFormTheme} is defensive so a partial /
 * legacy / hand-edited theme can never leave the form half-styled, and it is unit-tested directly.
 */
/** The out-of-the-box look: the "Classic" palette on "Soft" corners. */
export const DEFAULT_FORM_THEME = {
    bg: '#ffffff',
    surface: '#ffffff',
    soft: '#fbf9f5',
    text: '#151515',
    muted: '#6f6a63',
    border: '#e7e1d8',
    accent: '#6d4aff',
    accentTint: '#eee9ff',
    buttonBg: '#151515',
    buttonText: '#ffffff',
    radius: 10,
    inputRadius: 8,
    buttonRadius: 10,
};
/** The colour keys of a theme (a THEME template patches exactly these; a SHAPE template patches none). */
const COLOR_KEYS = [
    'bg',
    'surface',
    'soft',
    'text',
    'muted',
    'border',
    'accent',
    'accentTint',
    'buttonBg',
    'buttonText',
];
/** The radius keys of a theme (a SHAPE template patches exactly these). */
const RADIUS_KEYS = ['radius', 'inputRadius', 'buttonRadius'];
/** Corner-shape presets — patch only the three radii, keeping the current colours. */
export const SHAPE_TEMPLATES = [
    {
        id: 'square',
        name: 'Square',
        kind: 'shape',
        patch: { radius: 2, inputRadius: 2, buttonRadius: 2 },
    },
    {
        id: 'soft',
        name: 'Soft',
        kind: 'shape',
        patch: { radius: 8, inputRadius: 8, buttonRadius: 8 },
    },
    {
        id: 'elegant',
        name: 'Elegant',
        kind: 'shape',
        patch: { radius: 6, inputRadius: 6, buttonRadius: 6 },
    },
    {
        id: 'rounded',
        name: 'Rounded',
        kind: 'shape',
        patch: { radius: 13, inputRadius: 11, buttonRadius: 13 },
    },
    {
        id: 'pill',
        name: 'Pill',
        kind: 'shape',
        patch: { radius: 16, inputRadius: 999, buttonRadius: 999 },
    },
    {
        id: 'round',
        name: 'Full Round',
        kind: 'shape',
        patch: { radius: 20, inputRadius: 16, buttonRadius: 999 },
    },
];
/** Full colour themes — patch the palette, keep the seller's chosen shape. */
export const THEME_TEMPLATES = [
    {
        id: 'classic',
        name: 'Classic',
        description: 'Bouton noir · accent violet',
        kind: 'theme',
        patch: {
            bg: '#ffffff',
            surface: '#ffffff',
            soft: '#fbf9f5',
            text: '#151515',
            muted: '#6f6a63',
            border: '#e7e1d8',
            accent: '#6d4aff',
            accentTint: '#eee9ff',
            buttonBg: '#151515',
            buttonText: '#ffffff',
        },
    },
    {
        id: 'aurora',
        name: 'Aurora',
        description: 'Le dégradé maison aquilixShop',
        kind: 'theme',
        patch: {
            bg: '#ffffff',
            surface: '#ffffff',
            soft: '#f6f3ff',
            text: '#1a1430',
            muted: '#6f6a80',
            border: '#e6e0f5',
            accent: '#6d4aff',
            accentTint: '#eee9ff',
            buttonBg: 'linear-gradient(135deg,#6d4aff,#8b6bff 45%,#ff8a65 120%)',
            buttonText: '#ffffff',
        },
    },
    {
        id: 'emerald',
        name: 'Emerald',
        description: 'Vert confiance',
        kind: 'theme',
        patch: {
            bg: '#ffffff',
            surface: '#ffffff',
            soft: '#f2fbf6',
            text: '#123024',
            muted: '#5a7a68',
            border: '#d4ecdf',
            accent: '#18a058',
            accentTint: '#e4f5ec',
            buttonBg: '#18a058',
            buttonText: '#ffffff',
        },
    },
    {
        id: 'rubin',
        name: 'Rubin',
        description: 'Rouge urgence',
        kind: 'theme',
        patch: {
            bg: '#ffffff',
            surface: '#ffffff',
            soft: '#fef4f3',
            text: '#3a1512',
            muted: '#8a5a55',
            border: '#f3d9d5',
            accent: '#e5484d',
            accentTint: '#fde5e5',
            buttonBg: '#e5484d',
            buttonText: '#ffffff',
        },
    },
    {
        id: 'sapphire',
        name: 'Sapphire',
        description: 'Bleu premium',
        kind: 'theme',
        patch: {
            bg: '#f7faff',
            surface: '#ffffff',
            soft: '#eef4ff',
            text: '#12213a',
            muted: '#5a6a85',
            border: '#d8e4f5',
            accent: '#2a7fff',
            accentTint: '#e1ecff',
            buttonBg: '#2a7fff',
            buttonText: '#ffffff',
        },
    },
    {
        id: 'sable',
        name: 'Sable',
        description: 'Crème chaleureux',
        kind: 'theme',
        patch: {
            bg: '#faf8f5',
            surface: '#ffffff',
            soft: '#f3f0ea',
            text: '#2a2318',
            muted: '#7a7060',
            border: '#e7e1d8',
            accent: '#ff8a65',
            accentTint: '#ffe6d6',
            buttonBg: '#151515',
            buttonText: '#ffffff',
        },
    },
    {
        id: 'obsidian',
        name: 'Obsidian',
        description: 'Sombre chic',
        kind: 'theme',
        patch: {
            bg: '#141414',
            surface: '#1e1e1e',
            soft: '#242424',
            text: '#f4f4f4',
            muted: '#a0a0a0',
            border: '#333333',
            accent: '#8b6bff',
            accentTint: '#2a2440',
            buttonBg: '#ffffff',
            buttonText: '#151515',
        },
    },
];
/** All templates in one lookup, for `findTemplate` / `applyTemplateById`. */
const TEMPLATES_BY_ID = new Map([...SHAPE_TEMPLATES, ...THEME_TEMPLATES].map((t) => [t.id, t]));
export function findTemplate(id) {
    return TEMPLATES_BY_ID.get(id);
}
/**
 * Overlay a template's `patch` onto a base theme — the merge that "applying a template" performs.
 * Non-destructive: a shape template returns the same colours with new radii; a theme template returns
 * the same radii with new colours. Unknown ids are a no-op (returns the base unchanged).
 */
export function applyTemplate(base, template) {
    return { ...base, ...template.patch };
}
/** Convenience: apply a template by id (no-op if the id is unknown). */
export function applyTemplateById(base, id) {
    const t = findTemplate(id);
    return t ? applyTemplate(base, t) : base;
}
/** The CSS custom-property name each token maps to on the form root. */
const VAR_NAMES = {
    bg: '--cf-bg',
    surface: '--cf-surface',
    soft: '--cf-soft',
    text: '--cf-text',
    muted: '--cf-muted',
    border: '--cf-border',
    accent: '--cf-accent',
    accentTint: '--cf-accent-tint',
    buttonBg: '--cf-btn-bg',
    buttonText: '--cf-btn-text',
    radius: '--cf-radius',
    inputRadius: '--cf-input-radius',
    buttonRadius: '--cf-btn-radius',
};
const RADIUS_KEY_SET = new Set(RADIUS_KEYS);
/**
 * Resolve a theme to the `--cf-*` CSS custom-property map bound on the form root (via `[ngStyle]`),
 * exactly like the storefront theme resolvers (feature 0019). Radii become `Npx`; colours pass through.
 */
export function resolveThemeVars(theme) {
    const vars = {};
    Object.keys(VAR_NAMES).forEach((key) => {
        const value = theme[key];
        vars[VAR_NAMES[key]] = RADIUS_KEY_SET.has(key) ? `${value}px` : String(value);
    });
    return vars;
}
function isRecord(v) {
    return typeof v === 'object' && v !== null && !Array.isArray(v);
}
function colorOr(v, fallback) {
    return typeof v === 'string' && v.trim().length > 0 ? v : fallback;
}
function radiusOr(v, fallback) {
    return typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : fallback;
}
/**
 * Defensively parse a raw stored theme into a full {@link CodFormTheme}. Every token falls back to
 * {@link DEFAULT_FORM_THEME} when missing or of the wrong type, so a partial / legacy / hand-edited
 * theme can never render a broken form. Unknown keys are ignored.
 */
export function parseFormTheme(raw) {
    const obj = isRecord(raw) ? raw : {};
    const out = { ...DEFAULT_FORM_THEME };
    COLOR_KEYS.forEach((k) => {
        out[k] = colorOr(obj[k], DEFAULT_FORM_THEME[k]);
    });
    RADIUS_KEYS.forEach((k) => {
        out[k] = radiusOr(obj[k], DEFAULT_FORM_THEME[k]);
    });
    return out;
}
/** Serialize to the plain JSON object persisted alongside the COD config. */
export function serializeFormTheme(theme) {
    return { ...theme };
}
//# sourceMappingURL=cod-theme.js.map